import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Calendar, Award, Users, Clock, Eye, CheckCircle, AlertCircle } from "lucide-react";
import apiClient from "../../../utils/apiClient";
import { getCurrentUser } from "../../../utils/auth";
import { PageHeader, BackButton, AppBadge, AppButton, FilePreview, EmptyState, UserAvatar } from "../../ui";
import CommonToaster from "../../Common/CommonToaster";
import "./general-assignments.scss";

const submissionStatusConfig = {
  submitted: { label: "Submitted", color: "blue" },
  graded: { label: "Graded", color: "emerald" },
  resubmit_requested: { label: "Resubmit", color: "amber" },
};

const getRoutePrefix = () => {
  const user = getCurrentUser();
  return user?.usertype === "instructor" ? "/i/batch-assignments" : "/batch-assignments";
};

const GeneralAssignmentDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [assignment, setAssignment] = useState(null);
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState({ show: false, message: "", type: "success" });

  useEffect(() => {
    const fetch = async () => {
      setLoading(true);
      try {
        const [aRes, sRes] = await Promise.all([
          apiClient.get(`/general-assignments/detail/${id}`),
          apiClient.get(`/general-assignments/${id}/submissions`),
        ]);
        setAssignment(aRes.data.assignment);
        setSubmissions(sRes.data.submissions || []);
      } catch {
        setToast({ show: true, message: "Failed to load assignment", type: "error" });
      }
      setLoading(false);
    };
    fetch();
  }, [id]);

  const formatDate = (d) =>
    d ? new Date(d).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "—";

  if (loading) return <div className="ga-page ga-loading">Loading...</div>;
  if (!assignment) return <div className="ga-page"><BackButton /><EmptyState title="Assignment not found" /></div>;

  const isOverdue = new Date(assignment.dueDate) < new Date();
  const gradedCount = submissions.filter((s) => s.status === "graded").length;

  return (
    <div className="ga-page">
      <div className="ga-detail__header">
        <PageHeader
          backButton
          icon={Award}
          title={assignment.title}
          description={assignment.batchId?.name || ""}
          actions={
            <AppButton variant="outline" onClick={() => navigate(`${getRoutePrefix()}/edit/${id}`)}>
              Edit
            </AppButton>
          }
        />

        <div className="ga-detail__meta">
          <span className="ga-detail__meta-item">
            <Calendar size={15} /> Due: {formatDate(assignment.dueDate)}
          </span>
          <span className="ga-detail__meta-item">
            <Award size={15} /> Max Marks: {assignment.maxMarks}
          </span>
          <span className="ga-detail__meta-item">
            <Users size={15} /> {submissions.length} submission{submissions.length !== 1 ? "s" : ""}
          </span>
          <span className="ga-detail__meta-item">
            <CheckCircle size={15} /> {gradedCount} graded
          </span>
          <AppBadge variant="outline" color={isOverdue ? "rose" : "emerald"}>
            {isOverdue ? "Overdue" : "Open"}
          </AppBadge>
          <AppBadge variant="outline">{assignment.status}</AppBadge>
        </div>
      </div>

      {assignment.description && (
        <>
          <h4 className="ga-detail__section-title">Description</h4>
          <div className="ga-detail__description">{assignment.description}</div>
        </>
      )}

      {assignment.attachments?.length > 0 && (
        <>
          <h4 className="ga-detail__section-title">Attachments</h4>
          <FilePreview attachments={assignment.attachments} />
        </>
      )}

      <h4 className="ga-detail__section-title">
        Student Submissions ({submissions.length})
      </h4>

      {submissions.length === 0 ? (
        <EmptyState
          icon={<AlertCircle size={36} />}
          title="No submissions yet"
          description="Students haven't submitted this assignment yet"
        />
      ) : (
        <div className="ga-list__table-wrap">
          <table className="ga-detail__submissions-table">
            <thead>
              <tr>
                <th>Student</th>
                <th>Submitted</th>
                <th>Status</th>
                <th>Marks</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {submissions.map((s) => {
                const cfg = submissionStatusConfig[s.status] || {};
                return (
                  <tr key={s._id}>
                    <td>
                      <div className="ga-detail__student-info">
                        <UserAvatar name={s.studentId?.name} size={28} />
                        <span>{s.studentId?.name || "Unknown"}</span>
                      </div>
                    </td>
                    <td>
                      <span className="ga-detail__meta-item">
                        <Clock size={14} /> {formatDate(s.submittedAt)}
                      </span>
                    </td>
                    <td>
                      <AppBadge variant="outline" color={cfg.color}>
                        {cfg.label || s.status}
                      </AppBadge>
                    </td>
                    <td>
                      {s.marks != null ? `${s.marks}/${assignment.maxMarks}` : "—"}
                    </td>
                    <td>
                      <button
                        className="ga-list__action-btn"
                        title="Review"
                        onClick={() => navigate(`${getRoutePrefix()}/${id}/review/${s._id}`)}
                      >
                        <Eye size={16} />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <CommonToaster toast={toast} setToast={setToast} />
    </div>
  );
};

export default GeneralAssignmentDetail;
